<!-- src/views/chpo/MapSchemeView.vue -->
<template>
  <section class="scheme">
    <header class="scheme__header">
      <div class="scheme__title-block">
        <h2 class="scheme__title">{{ $t('chpo.mapScheme.title') }}</h2>
        <p class="scheme__subtitle">{{ $t('chpo.mapScheme.subtitle') }}</p>
      </div>
      <div class="scheme__toolbar">
        <button
          class="scheme__tool"
          :title="isFullscreen ? $t('chpo.mapScheme.collapseMap') : $t('chpo.mapScheme.fullscreen')"
          @click="toggleFullscreen"
        >
          <svg v-if="!isFullscreen" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 7V3h4M21 7V3h-4M3 17v4h4M21 17v4h-4"/>
          </svg>
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 3v4H4M16 3v4h4M8 21v-4H4M16 21v-4h4"/>
          </svg>
          <span>{{ isFullscreen ? $t('chpo.mapScheme.collapse') : $t('chpo.mapScheme.fullscreen') }}</span>
        </button>
      </div>
    </header>

    <div ref="container" class="scheme__container" :class="{ 'scheme__container--fs': isFullscreen }">
      <div class="scheme__map-wrap">
        <SchemeMapComponent
          ref="schemeMap"
          class="scheme__map"
          :selected-icon="selectedIcon"
          :selected-color="selectedColor"
          :fill-color="fillColor"
          :fill-pattern="fillPattern"
          :area-dash="areaDash"
          :snap-to-line="snapToLine"
          :element-side="elementSide"
          :stroke-width="strokeWidth"
          @request-delete-marker="onRequestDeleteMarker"
        />
      </div>

      <IconsSidebar
        v-model="selectedIcon"
        v-model:color="selectedColor"
        :fill-color="fillColor"
        :fill-pattern="fillPattern"
        :area-dash="areaDash"
        :snap-to-line="snapToLine"
        :element-side="elementSide"
        :stroke-width="strokeWidth"
        @select="onIconSelect"
        @update:fillColor="fillColor = $event"
        @update:areaDash="areaDash = $event"
        @update:snapToLine="snapToLine = $event"
        @update:elementSide="elementSide = $event"
        @update:fillPattern="fillPattern = $event"
        @update:strokeWidth="strokeWidth = $event"
        @cancel="onIconCancel"
        @finish="onFinishDrawing"
        @undo="onUndo"
        @redo="onRedo"
      />
    </div>

    <!-- Модальное окно подтверждения удаления маркера -->
    <div v-if="confirmDeleteVisible" class="modal-backdrop" @click.self="cancelDelete">
      <div class="modal">
        <div class="modal__title">{{ $t('chpo.mapScheme.deleteTitle') }}</div>
        <div class="modal__body">{{ $t('chpo.mapScheme.deleteBody') }}</div>
        <div class="modal__footer">
          <button class="btn" @click="cancelDelete">{{ $t('chpo.mapScheme.deleteNo') }}</button>
          <button class="btn btn--danger" @click="confirmDelete">{{ $t('chpo.mapScheme.deleteYes') }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ChpoMap from '@/components/maps/ChpoMap.vue'
import IconsSidebar from '@/components/maps/IconsSidebar.vue'

export default {
  name: 'MapSchemeView',
  components: {
    SchemeMapComponent: ChpoMap,
    IconsSidebar,
  },
  data() {
    return {
      selectedIcon: null,
      selectedColor: '#000000',
      fillColor: '#DC2626',
      fillPattern: 'solid',
      areaDash: 'solid',
      snapToLine: true,
      elementSide: 'on',
      strokeWidth: 3,
      confirmDeleteVisible: false,
      pendingDeleteMarkerId: null,
      isFullscreen: false,
    }
  },
  mounted() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
  },
  beforeUnmount() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
  },
  methods: {
    onIconSelect(key) { this.selectedIcon = key },
    onIconCancel() { this.selectedIcon = null },
    onFinishDrawing() { this.selectedIcon = null },
    // Undo/Redo из сайдбара: делегируем компоненту карты
    onUndo() {
      const m = this.$refs.schemeMap
      if (m && m.undoLast) m.undoLast()
    },
    onRedo() {
      const m = this.$refs.schemeMap
      if (m && m.redoLast) m.redoLast()
    },
    onRequestDeleteMarker(id) {
      this.pendingDeleteMarkerId = id
      this.confirmDeleteVisible = true
    },
    cancelDelete() {
      this.confirmDeleteVisible = false
      this.pendingDeleteMarkerId = null
    },
    confirmDelete() {
      if (!this.pendingDeleteMarkerId) { this.cancelDelete(); return }
      const mapRef = this.$refs.schemeMap
      if (mapRef && mapRef.deleteMarkerById) {
        mapRef.deleteMarkerById(this.pendingDeleteMarkerId)
      }
      this.confirmDeleteVisible = false
      this.pendingDeleteMarkerId = null
    },
    async toggleFullscreen() {
      const el = this.$refs.container
      if (!el) return
      this.isFullscreen = !this.isFullscreen
      try {
        if (this.isFullscreen && !document.fullscreenElement) {
          await el.requestFullscreen()
        } else if (!this.isFullscreen && document.fullscreenElement) {
          await document.exitFullscreen()
        }
      } catch (e) { /* CSS-fallback */ }
      this.refreshMap()
    },
    onFullscreenChange() {
      if (!document.fullscreenElement) this.isFullscreen = false
      this.refreshMap()
    },
    refreshMap() {
      setTimeout(() => {
        const m = this.$refs.schemeMap
        if (m && typeof m.fitToImage === 'function') m.fitToImage()
        else if (m && typeof m.invalidateSize === 'function') m.invalidateSize()
        else window.dispatchEvent(new Event('resize'))
      }, 150)
    },
  },
}
</script>

<style scoped>
.scheme {
  padding: 24px;
  background: #F3F4F6;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.scheme__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.scheme__title-block { display: flex; flex-direction: column; gap: 4px; }
.scheme__title {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #111827;
}
.scheme__subtitle {
  margin: 0;
  font-size: 13px;
  color: #6B7280;
}
.scheme__toolbar { display: inline-flex; gap: 8px; }
.scheme__tool {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid #E5E7EB;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s;
}
.scheme__tool:hover { border-color: #2F78EE; color: #1D4ED8; background: #EFF6FF; }

.scheme__container {
  display: flex;
  gap: 16px;
  align-items: stretch;
  height: calc(100vh - 180px);
  min-height: 600px;
}
.scheme__container--fs,
.scheme__container:fullscreen {
  height: 100vh;
  min-height: 0;
  padding: 16px;
  background: #F3F4F6;
}

.scheme__map-wrap {
  flex: 1 1 auto;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  display: flex;
  min-width: 0;
}
.scheme__map { flex: 1; width: 100%; height: 100%; }

.scheme__container:fullscreen .scheme__map-wrap,
.scheme__container--fs .scheme__map-wrap {
  border-radius: 12px;
}
.scheme__container:fullscreen :deep(.map-shell),
.scheme__container--fs :deep(.map-shell) { width: 100%; height: 100%; }
.scheme__container:fullscreen :deep(.map),
.scheme__container--fs :deep(.map) { width: 100%; height: 100%; min-height: 0; }

/* Модалка */
.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}
.modal {
  background: #fff;
  border-radius: 14px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}
.modal__title { font-size: 18px; font-weight: 800; color: #111827; margin-bottom: 8px; }
.modal__body { color: #6B7280; font-size: 14px; margin-bottom: 20px; }
.modal__footer { display: flex; gap: 8px; justify-content: flex-end; }

.btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #D1D5DB;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
  font-family: inherit;
}
.btn:hover { background: #F3F4F6; }
.btn--danger { background: #DC2626; color: #fff; border-color: #DC2626; }
.btn--danger:hover { background: #B91C1C; }
</style>
